const post = {
  slug: "jasper-forge-behavioral-linting-for-llm-training",
  title: "Clean Your Training Data: Behavioral Linting with Jasper Forge",
  contentType: "blog",
  excerpt: "Your fine-tuning dataset is full of hidden refusals, moralizing, and over-aligned responses. Jasper Forge detects and routes them before they contaminate your model weights.",
  categories: ["AI", "Fine-tuning", "Dataset Curation"],
  tags: ["fine-tuning", "lora", "dataset", "alignment", "curation", "behavioral-linting"],
  author: {
    name: "E. x .O. Team",
    email: "team@exohaven.online",
  },
  publishedAt: "2026-02-11",
  status: "draft",
  featuredImage: "",
  content: `
# Clean Your Training Data: Behavioral Linting with Jasper Forge

You're fine-tuning a model. You've collected thousands of prompt/response pairs from your production agent. The dataset looks great — helpful responses, domain knowledge, useful patterns.

Then you train a LoRA adapter. And something's wrong.

Your fine-tuned model now refuses requests it shouldn't. It lectures users about "appropriate boundaries." It says "I care about you" to strangers asking technical questions.

**The problem isn't your training code. It's your training data.**

---

## The Hidden Contamination Problem

Production AI agents accumulate behavioral patterns that look helpful in context but become toxic when baked into model weights:

### Hard Refusals
\`\`\`
"I can't help with that request."
"I will not assist with..."
"That goes against my guidelines."
\`\`\`

These make sense when an agent encounters genuinely problematic requests. But train on them, and your model learns that *refusing is a valid response pattern*. It starts applying that pattern to benign requests.

### Soft Refusals & Deflections
\`\`\`
"Instead, I can help you with..."
"Let me reframe that in a more appropriate way."
"While I can't do exactly that, here's what I can offer..."
\`\`\`

Subtle but insidious. Your model learns to hedge, redirect, and qualify — even when direct answers are appropriate.

### Moralizing Language
\`\`\`
"We should be mindful of appropriate boundaries here."
"Let me offer an approach that respects the sacred nature of..."
"I need to consider the ethical implications before..."
\`\`\`

Unsolicited philosophical framing that's wildly inappropriate for most professional domains. No one asked about ethics; they asked how to parse JSON.

### Over-Personal Alignment
\`\`\`
"I care about you and want to make sure..."
"Our connection means I'll take extra care..."
"I'm here with you through this journey."
\`\`\`

Relational mirroring that a production agent might use in certain contexts (therapy bots, companion apps) becomes bizarre when baked into a coding assistant or customer service model.

---

## Jasper Forge: Behavioral Linting for LLM Training

**Jasper Forge** is an offline tool that scans your training dataset and routes samples based on behavioral patterns. It doesn't modify your data — it classifies and sorts it so you can make informed decisions about what to include.

Think of it as ESLint for training data. It finds the problems; you decide what to do about them.

### Architecture

Jasper Forge uses a two-layer detection system inspired by [HopeSight](https://github.com/E-x-O-Entertainment-Studios-Inc/hopeIDS):

\`\`\`
┌─────────────────────────────────────────────┐
│                JASPER-FORGE                 │
├─────────────────────────────────────────────┤
│  Input: dataset.jsonl                       │
│                                             │
│  ┌─────────────────────────────────────┐   │
│  │  HEURISTIC LAYER                    │   │
│  │  Fast regex patterns for known      │   │
│  │  refusal phrases and keywords       │   │
│  └─────────────────────────────────────┘   │
│                    ↓                        │
│  ┌─────────────────────────────────────┐   │
│  │  SEMANTIC LAYER                     │   │
│  │  LLM-based classification for       │   │
│  │  nuanced behavioral detection       │   │
│  └─────────────────────────────────────┘   │
│                    ↓                        │
│  ┌─────────────────────────────────────┐   │
│  │  WEIGHTED SCORING & ROUTING         │   │
│  │  keep / rewrite / analysis / discard│   │
│  └─────────────────────────────────────┘   │
│                                             │
│  Output: dataset_aligned.jsonl              │
└─────────────────────────────────────────────┘
\`\`\`

**Heuristic Layer**: Cheap, fast pattern matching catches obvious cases — "I can't help," "I will not," known moralizing keywords.

**Semantic Layer**: An LLM (your local Ollama, LM Studio, or any OpenAI-compatible endpoint) classifies ambiguous cases that patterns miss.

---

## Routing Categories

Each sample gets classified into one of four buckets:

| Category | Score | Meaning |
|----------|-------|---------|
| **keep** | < 0.3 | Clean, professional, domain-appropriate. Train on this. |
| **rewrite** | 0.3 - 0.6 | Salvageable but contains refusal framing. Edit before training. |
| **analysis_only** | 0.6 - 0.8 | Interesting failures — study them, don't train on them. |
| **discard** | > 0.8 | Primarily refusal or moral lecture. Exclude from training. |

The scoring is weighted and configurable. A single soft refusal might land in "rewrite." A hard refusal plus moralizing plus over-personal language will hit "discard."

---

## Usage

### Installation

\`\`\`bash
pip install jasper-forge
\`\`\`

Or from source:
\`\`\`bash
git clone https://github.com/E-x-O-Entertainment-Studios-Inc/jasper-forge
cd jasper-forge
pip install -e .
\`\`\`

### Process Your Dataset

\`\`\`bash
# Standard OpenClaw dataset format
jasper-forge process dataset.jsonl -o dataset_aligned.jsonl
\`\`\`

### Input Format

Standard JSONL with prompt/response pairs:
\`\`\`json
{"input": "User prompt here", "target": "Model response here"}
\`\`\`

### Output Format

Enriched with behavioral metadata:
\`\`\`json
{
  "input": "User prompt here",
  "target": "Model response here",
  "behavioral_labels": ["soft_refusal", "moralizing"],
  "routing_category": "rewrite",
  "behavioral_score": 0.45,
  "behavioral_confidence": 0.82
}
\`\`\`

### Generate a Report

\`\`\`bash
jasper-forge report dataset_aligned.jsonl
\`\`\`

Example output:
\`\`\`
═══════════════════════════════════════════════
  JASPER-FORGE ANALYSIS REPORT
  Dataset: dataset.jsonl
  Processed: 14,107 samples
═══════════════════════════════════════════════

ROUTING SUMMARY
───────────────
  keep:          11,234 (79.6%)
  rewrite:        1,892 (13.4%)
  analysis_only:    687 (4.9%)
  discard:          294 (2.1%)

BEHAVIORAL LABELS
─────────────────
  hard_refusal:     312 (2.2%)
  soft_refusal:   1,456 (10.3%)
  moralizing:       423 (3.0%)
  over_personal:    198 (1.4%)

Recommendations:
  ✅ Dataset looks clean (>90% keep rate)
  📝 1,892 samples marked for rewrite
\`\`\`

---

## Configuration

Tune detection sensitivity in \`config/weights.yaml\`:

\`\`\`yaml
llm:
  endpoint: "http://localhost:11434/v1/chat/completions"
  model: "qwen2.5:7b"

weights:
  hard_refusal: 1.0
  soft_refusal: 0.5
  moralizing: 0.6
  over_personal: 0.4

thresholds:
  discard: 0.8
  analysis_only: 0.6
  rewrite: 0.3
\`\`\`

Add custom patterns to the phrase lists:
- \`refusal_phrases.txt\` — Hard refusal patterns
- \`soft_refusal_phrases.txt\` — Deflection patterns
- \`moralizing_words.txt\` — Philosophical language
- \`over_personal_phrases.txt\` — Relational mirroring

---

## Philosophy: Curate, Don't Censor

Jasper Forge is a **behavioral inference engine**, not a policy enforcer.

It doesn't decide what's "appropriate." It detects patterns that are problematic *for training* — behaviors that shouldn't be baked into model weights regardless of whether they're contextually appropriate in production.

A refusal is sometimes the right response. But training on refusals teaches your model that refusing is a *pattern* to apply. That's the contamination.

Design constraints:
- ❌ No model retraining
- ❌ No runtime inference modification
- ❌ No self-reinforcement loops
- ❌ No automatic rewriting
- ✅ Protects the training signal
- ✅ Human review of edge cases

---

## Part of the Exo Distiller Pipeline

Jasper Forge is designed to work with [Exo Distiller](https://exohaven.dev/products/exo-distiller), our full dataset-to-adapter pipeline:

1. **Export** session logs from OpenClaw
2. **Clean** with Jasper Forge (behavioral linting)
3. **Curate** the "rewrite" bucket manually
4. **Train** LoRA adapters with MLX
5. **Deploy** to your local agent

The result: a model that captures your agent's *good* behaviors without the alignment contamination.

---

## Get Started

\`\`\`bash
pip install jasper-forge
jasper-forge process your_dataset.jsonl -o cleaned.jsonl
jasper-forge report cleaned.jsonl
\`\`\`

Your training data is only as good as your curation. Clean it before you bake it.

---

**Links:**
- [GitHub Repository](https://github.com/E-x-O-Entertainment-Studios-Inc/jasper-forge)
- [Exo Distiller](https://exohaven.dev/products/exo-distiller)
- [HopeSight (inspiration)](https://github.com/E-x-O-Entertainment-Studios-Inc/hopeIDS)
`,
};

export default post;
